import { useRuntimeVariables } from 'app-kit'
import { type AuthedAxiosRequests, useGeckoAuth } from 'features/auth'
import type { DocumentSubjectsResponse } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import { useGeckoPermissions } from 'features/permissions'
import { useCallback } from 'react'
import useSWR, { preload } from 'swr'
import {
  PERMISSION_CANTILEVER_POWER_SUBJECTS_VIEWER,
  PERMISSION_GECKO_INTERNAL_MEMBER,
} from 'types'

const subjectsEndpoint = (
  fimServiceUrl: string,
  site: string,
  documentId: string
) =>
  `${fimServiceUrl}/api/v1/sites/${encodeURIComponent(
    site
  )}/documents/${encodeURIComponent(
    documentId
  )}/subjects?include_doc_references=true`

const subjectsFetcher =
  (axios: AuthedAxiosRequests) =>
  async (url: string): Promise<DocumentSubjectsResponse> => {
    const res = await axios.get(url, {
      headers: { 'Content-Type': 'application/json' },
    })
    return res.data
  }

/** Site-wide subjects fetch per document (no page param); skipped without subjects permission. */
export function useDocumentSubjects(
  site: string | undefined,
  documentId: string | undefined
) {
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { hasPermissions } = useGeckoPermissions()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const canSeeSubjects = hasPermissions([
    PERMISSION_GECKO_INTERNAL_MEMBER,
    PERMISSION_CANTILEVER_POWER_SUBJECTS_VIEWER,
  ])

  const { data, error, isLoading } = useSWR<DocumentSubjectsResponse, Error>(
    isAuthenticated && canSeeSubjects && site && documentId
      ? subjectsEndpoint(FIM_SERVICE_URL, site, documentId)
      : null,
    subjectsFetcher(axios)
  )

  return { data, error, isLoading }
}

/** Preload subjects via the same SWR key as useDocumentSubjects. */
export function useDocumentSubjectsPreload() {
  const { authorizedAxios: axios, isAuthenticated } = useGeckoAuth()
  const { hasPermissions } = useGeckoPermissions()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const canSeeSubjects = hasPermissions([
    PERMISSION_GECKO_INTERNAL_MEMBER,
    PERMISSION_CANTILEVER_POWER_SUBJECTS_VIEWER,
  ])

  return useCallback(
    (
      site: string | undefined,
      documentId: string | undefined
    ): Promise<DocumentSubjectsResponse> | null => {
      if (!isAuthenticated || !canSeeSubjects || !site || !documentId)
        return null
      return preload(
        subjectsEndpoint(FIM_SERVICE_URL, site, documentId),
        subjectsFetcher(axios)
      )
    },
    [axios, isAuthenticated, canSeeSubjects, FIM_SERVICE_URL]
  )
}
